import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import CookieBanner from '@/components/CookieBanner';
import MobileStickyBar from '@/components/MobileStickyBar';
import Analytics from '@/components/Analytics';
import { AuthProvider } from '@/context/AuthContext';
import { SiteSettingsProvider } from '@/context/SiteSettingsContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { CartProvider } from '@/context/CartContext';
import { Toaster } from '@/components/ui/sonner';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import OrderSuccess from '@/pages/OrderSuccess';
import PartnerLocations from '@/pages/PartnerLocations';
import Rental from '@/pages/Rental';
import Training from '@/pages/Training';
import GuideDetail from '@/pages/GuideDetail';
import RentalSuccess from '@/pages/RentalSuccess';

import Home from '@/pages/Home';
import Services from '@/pages/Services';
import Industries from '@/pages/Industries';
import Machines from '@/pages/Machines';
import MachineDetail from '@/pages/MachineDetail';
import Projects from '@/pages/Projects';
import ProjectDetail from '@/pages/ProjectDetail';
import FAQ from '@/pages/FAQ';
import SurfaceSolutionFinder from '@/pages/SurfaceSolutionFinder';
import Careers from '@/pages/Careers';
import Partners from '@/pages/Partners';
import PartnersApply from '@/pages/PartnersApply';
import Referrals from '@/pages/Referrals';
import KnowledgeCenter from '@/pages/KnowledgeCenter';
import Location from '@/pages/Location';
import Videos from '@/pages/Videos';
import News from '@/pages/News';
import NewsDetail from '@/pages/NewsDetail';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import { Privacy, Cookies, Terms, Disclaimer } from '@/pages/Legal';

const NotFound = () => (
    <div className="container-page pt-32 pb-24" data-testid="not-found-page">
        <div className="label-caps text-brand-primary mb-4">404</div>
        <h1 className="font-display font-black uppercase text-5xl md:text-7xl">Page not found</h1>
        <p className="mt-4 text-white/60">The page you were looking for isn&apos;t here.</p>
    </div>
);

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <ThemeProvider>
                    <AuthProvider>
                        <SiteSettingsProvider>
                        <CartProvider>
                        <ScrollToTop />
                        <Analytics />
                        <Header />
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/services" element={<Services />} />
                            <Route path="/industries" element={<Industries />} />
                            <Route path="/machines" element={<Machines />} />
                            <Route path="/shop" element={<Machines />} />
                            <Route path="/cart" element={<Cart />} />
                            <Route path="/checkout" element={<Checkout />} />
                            <Route path="/order/success" element={<OrderSuccess />} />
                            <Route path="/partner-locations" element={<PartnerLocations />} />
                            <Route path="/rental" element={<Rental />} />
                            <Route path="/training" element={<Training />} />
                            <Route path="/rental/success" element={<RentalSuccess />} />
                            <Route path="/machines/:key" element={<MachineDetail />} />
                            <Route path="/projects" element={<Projects />} />
                            <Route path="/projects/:key" element={<ProjectDetail />} />
                            <Route path="/faq" element={<FAQ />} />
                            <Route path="/surface-solution-finder" element={<SurfaceSolutionFinder />} />
                            <Route path="/machine-finder" element={<SurfaceSolutionFinder />} />
                            <Route path="/careers" element={<Careers />} />
                            <Route path="/partners" element={<Partners />} />
                            <Route path="/partners/apply" element={<PartnersApply />} />
                            <Route path="/network" element={<Referrals />} />
                            <Route path="/knowledge" element={<KnowledgeCenter />} />
                            <Route path="/knowledge/:slug" element={<GuideDetail />} />
                            <Route path="/locations/:city" element={<Location />} />
                            <Route path="/videos" element={<Videos />} />
                            <Route path="/news" element={<News />} />
                            <Route path="/news/:key" element={<NewsDetail />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/cookies" element={<Cookies />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/disclaimer" element={<Disclaimer />} />
                            <Route path="/admin" element={<AdminLogin />} />
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin/dashboard" element={<AdminDashboard />} />

                            {/* Legacy WordPress URL redirects — preserve SEO ranking from dryicebulls.com
                                Old site: WordPress at /home/, /product/*, /contact-us/, etc.
                                Google will pick up the new canonical URLs on next crawl. */}
                            <Route path="/home" element={<Navigate to="/" replace />} />
                            <Route path="/home/" element={<Navigate to="/" replace />} />
                            <Route path="/home/contact-us" element={<Navigate to="/contact" replace />} />
                            <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
                            <Route path="/product/dry-ice-cleaner-ic-430" element={<Navigate to="/machines/dry-ice-cleaner-ic-430" replace />} />
                            <Route path="/product/dry-ice-cleaner-ic-410" element={<Navigate to="/machines/dry-ice-cleaner-ic-410s" replace />} />
                            <Route path="/product/dry-ice-cleaner-ic-310s" element={<Navigate to="/machines/dry-ice-cleaner-ic-310s" replace />} />
                            <Route path="/product/dry-ice-cleaner-ic-110-e" element={<Navigate to="/machines/dry-ice-cleaner-ic-110-e" replace />} />
                            <Route path="/product/dry-ice-cleaner-ic-039" element={<Navigate to="/machines/dry-ice-cleaner-ic-030" replace />} />
                            <Route path="/product/dry-ice-cleaner-ic-030" element={<Navigate to="/machines/dry-ice-cleaner-ic-030" replace />} />
                            <Route path="/product/dry-ice-cleaner-ic-022" element={<Navigate to="/machines/dry-ice-cleaner-ic-022" replace />} />
                            <Route path="/product/flex-case-i-nozzle-kit" element={<Navigate to="/machines/flex-case-i-nozzle-kit" replace />} />
                            <Route path="/product/flex-case-ii-nozzle-kit" element={<Navigate to="/machines/flex-case-ii-nozzle-kit" replace />} />
                            <Route path="/product/external-abrasive-blasting-module-pam-01" element={<Navigate to="/machines/external-abrasive-blasting-module-pam-01" replace />} />
                            <Route path="/product/external-abrasive-blasting-module-pam-02" element={<Navigate to="/machines/external-abrasive-blasting-module-pam-02" replace />} />
                            <Route path="/product/external-abrasive-blasting-module-pam-03" element={<Navigate to="/machines/external-abrasive-blasting-module-pam-03" replace />} />
                            {/* Catch-all for any unrecognized /product/* → send to machines catalog */}
                            <Route path="/product/*" element={<Navigate to="/machines" replace />} />

                            <Route path="*" element={<NotFound />} />
                        </Routes>
                        <Footer />
                        <MobileStickyBar />
                        <CookieBanner />
                        <Toaster theme="light" position="bottom-right" richColors />
                        </CartProvider>
                    </SiteSettingsProvider>
                </AuthProvider>
                </ThemeProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;

